/* eslint-disable react-hooks/exhaustive-deps */
import { DrawInfo } from "../../../../models/DrawInfo";
import { USDC } from "../../../../models/UsdcValue";
import { TicketMapper } from "../../../../models/TicketMapper";
import {
  CURRENCY,
  ANIMATION_DELAY_MULTIPLIER,
  TICKET_CARD_ERROR_PULSING_TIME_MS,
} from "../../../../utils/Constants";
import Button from "../../../miscellaneous/Button";
import TicketCard from "../../../miscellaneous/TicketCard";
import ball from "../../../../assets/images/persian_pink_ball.png";
import { ButtonTheme } from "../../../miscellaneous/ButtonTheme";
import { useEffect, useRef, useState } from "react";
import { snackBar, truncateMiddle } from "../../../../utils/Utils";
import { useRive, useStateMachineInput } from "@rive-app/react-canvas";
import { FaCheck } from "react-icons/fa";
import { Address, isAddress } from "viem";
import { useSearchParams } from "react-router-dom";
import coupon from "../../../../assets/animations/coupon.riv";

export interface TicketsProps {
  /**
   * @description Whether a wallet is currently connected or not.
   */
  isConnected: boolean;
  /**
   * @description Whether a wallet is currently connecting or not.
   */
  isConnecting: boolean;
  /**
   * @description Callback to when the ConnectWallet button is clicked.
   */
  connectWallet: () => Promise<void>;
  /**
   * @description Array of ticket mappers.
   */
  ticketMappers: TicketMapper[];
  /**
   * @description Callback to buy tickets. Receives an array
   * of {@link TicketMapper} objects.
   */
  onBuyTickets: (
    ticketMappers: TicketMapper[],
    referrer: Address | undefined,
    useCoupon: boolean,
  ) => Promise<void>;
  /**
   * @description Whether buying is enabled or not.
   */
  buyingEnabled: boolean;
  /**
   * @description Whether buying is currently in progress or not.
   */
  buying: boolean;
  /**
   * @description Whether the ticket purchasing is opened or not.
   */
  isOpened: boolean;
  /**
   * @description Callback to generate a lucky dip. Receives the
   * index of the ticket to lucky dip.
   */
  onLuckyDip: (index: number) => void;
  /**
   * @description Callback to clear a ticket. Receives the index
   * of the ticket to clear.
   */
  onClear: (index: number) => void;
  /**
   * @description Callback when clicking a number. Receives the index
   * of the ticket, the number, and whether it is selected or not.
   */
  onClickNumber: (index: number, number: number, selected: boolean) => void;
  /**
   * @description Callback when clicking a star. Receives the index
   * of the ticket, the star, and whether it is selected or not.
   */
  onClickStar: (index: number, star: number, selected: boolean) => void;
  /**
   * @description Draw information.
   */
  drawInfo: DrawInfo;
  /**
   * @description Draw date.
   */
  drawDate?: Date;
  /**
   * @description Ticket buy limit.
   */
  ticketLimitPerPurchase: bigint;
  /**
   * @description Amount of points the user has.
   */
  points: number;
}

export default function Tickets(props: TicketsProps) {
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get("ref");

  const [usingCoupon, setUsingCoupon] = useState<boolean>(false);

  const connectingBeforeBuyTickets = useRef(false);

  // Get ticket price from Draw information.
  const ticketPrice: USDC = new USDC(
    props.drawInfo?.drawConfig.ticketPrice ?? 0n,
  );

  // Get the number of complete tickets.
  const ticketsComplete: number = props.ticketMappers.filter((ticketMapper) =>
    ticketMapper.isComplete(
      Number(props.drawInfo?.drawConfig.numbersPerTicket ?? 0n),
      Number(props.drawInfo?.drawConfig.starsPerTicket ?? 0n),
    ),
  ).length;

  // Total price of the currently complete tickets.
  const basePrice = ticketsComplete * Number(ticketPrice.value);
  const discount = usingCoupon
    ? Number(props.ticketLimitPerPurchase - 1n) /
      Number(props.ticketLimitPerPurchase)
    : 1;
  const ethValue = new USDC(BigInt(basePrice * discount));

  const [pulsing, setPulsing] = useState<boolean[]>(
    Array(props.ticketMappers.length).fill(false),
  );

  const buyTicketsClickable: boolean =
    ticketsComplete > 0 && props.buyingEnabled && !props.isConnecting;

  async function buyTickets() {
    // Check if there's any tickets that are started but incomplete
    const invalidTickets: TicketMapper[] = props.ticketMappers.filter(
      (ticketMapper) =>
        !ticketMapper.isValid(
          Number(props.drawInfo.drawConfig.numbersPerTicket),
          Number(props.drawInfo.drawConfig.starsPerTicket),
        ),
    );
    if (invalidTickets.length > 0) {
      setPulsing((pulsing) =>
        pulsing.map((_, index) =>
          invalidTickets.some((ticketMapper) => ticketMapper.id === index),
        ),
      );
      setTimeout(
        () => setPulsing((pulsing) => pulsing.map(() => false)),
        TICKET_CARD_ERROR_PULSING_TIME_MS,
      );
      snackBar("You have incomplete tickets.", "❌");
    } else {
      props.onBuyTickets(
        props.ticketMappers,
        referrer && isAddress(referrer) ? (referrer as Address) : undefined,
        usingCoupon,
      );
    }
  }

  const onBuyTickets = () => {
    if (!props.isOpened) {
      snackBar(
        "Drawing is in progress. Purchases are disabled right now.",
        "❌",
      );
    } else {
      if (!props.isConnected) {
        connectingBeforeBuyTickets.current = true;
        props.connectWallet();
      } else {
        buyTickets();
      }
    }
  };

  useEffect(() => {
    if (props.isConnected && connectingBeforeBuyTickets.current) {
      connectingBeforeBuyTickets.current = false;
      setTimeout(() => {
        buyTickets();
      }, 1000);
    }
  }, [props.isConnected]);

  const { rive, RiveComponent } = useRive({
    src: coupon,
    stateMachines: "state_machine",
    autoplay: true,
  });

  const toIdle = useStateMachineInput(rive, "state_machine", "to_idle");
  const toActivated = useStateMachineInput(
    rive,
    "state_machine",
    "to_activated",
  );

  function onCouponClick() {
    if (usingCoupon) {
      toIdle?.fire();
    } else {
      toActivated?.fire();
    }
    setUsingCoupon(!usingCoupon);
  }

  useEffect(() => {
    if (ticketsComplete == 0) {
      setUsingCoupon(false);
      toIdle?.fire();
    }
  }, [ticketsComplete]);

  return (
    <div className="w-full flex flex-col xl:items-center">
      {/**
       * Ticket section. Contains all the ticket cards.
       */}
      <div className="w-full overflow-x-auto no-scrollbar">
        <div className="w-full min-w-max pt-[32px] flex flex-row lg:justify-center justify-start overflow-y-hidden">
          {props.ticketMappers.map((ticketMapper, index) => (
            <div
              key={index}
              className={`mb-[16px] ${index == 0 ? "ml-[28px]" : "ml-[8px]"} ${index == props.ticketMappers.length - 1 ? "mr-[28px]" : "mr-[8px]"}`}
              data-aos="fade-left"
              data-aos-delay={index * ANIMATION_DELAY_MULTIPLIER}
            >
              <TicketCard
                key={index}
                onClickNumber={function (
                  number: number,
                  selected: boolean,
                ): void {
                  props.onClickNumber(index, number, selected);
                }}
                onClickStar={function (star: number, selected: boolean): void {
                  props.onClickStar(index, star, selected);
                }}
                onLuckyDip={() => props.onLuckyDip(index)}
                onLuckyDipAll={() => {
                  props.ticketMappers.forEach((_, i) => {
                    props.onLuckyDip(i);
                  });
                }}
                onClear={() => props.onClear(index)}
                ticket={ticketMapper}
                drawInfo={props.drawInfo}
                pulsing={pulsing[index]}
              ></TicketCard>
            </div>
          ))}
        </div>
      </div>
      {/**
       * Button to buy tickets. Calls {@link onBuyTickets}.
       */}
      <div
        className="w-full flex md:flex-row flex-col-reverse self-center justify-center items-center ~pt-1/2"
        data-aos="fade"
      >
        {ticketsComplete > 0 &&
        referrer == undefined &&
        props.points >= props.ticketLimitPerPurchase ? (
          <div
            className={`select-none flex flex-row flex-1 min-w-0 justify-end items-center`}
          >
            <div
              className={`cursor-pointer hover:scale-105 duration-250 flex flex-row items-center pr-3 pl-2 rounded-lg ${usingCoupon ? "bg-bastille" : "bg-cinder shadow-lg shadow-bastille"}`}
              onClick={onCouponClick}
            >
              <div className="w-[40px] h-[40px] flex-none">
                <RiveComponent />
              </div>
              <div className="w-[2px]"></div>
              <span
                className={`font-poppins ~text-sm/base font-bold ${usingCoupon ? "text-waterloo" : "text-chatelle animate-pulse"}`}
              >
                {usingCoupon ? "Coupon" : "Use coupon?"}
              </span>
              <FaCheck
                className={`ml-2 transition-transform duration-1000 ease-in-out ${usingCoupon ? "text-waterloo w-5 scale-100" : "text-chatelle w-0 scale-0"}`}
              ></FaCheck>
            </div>
          </div>
        ) : (
          <div className="select-none flex flex-row flex-1 min-w-0 justify-end items-center"></div>
        )}
        <div className="flex-none flex flex-row mx-4 w-full md:w-fit md:mb-0 mb-2">
          <div className="flex-1 select-none md:hidden block" />
          <div className="relative">
            <Button
              text={
                props.buyingEnabled
                  ? ticketsComplete > 0
                    ? `BUY ${ticketsComplete} TICKET${ticketsComplete > 1 ? "S" : ""} (${ethValue.pretty} ${CURRENCY})`
                    : "BUY TICKETS"
                  : "DRAW STARTING SOON"
              }
              theme={ButtonTheme.PersianPink}
              loading={props.buying}
              enabled={buyTicketsClickable}
              onClick={onBuyTickets}
            ></Button>
            {referrer != undefined && (
              <div
                className={`absolute ml-[6px] -z-20 flex flex-row items-center left-auto right-auto bg-bastille rounded-b-lg w-fit transition-all duration-500 overflow-hidden ${
                  buyTicketsClickable
                    ? "max-h-40 px-[4px] py-[4px] top-[40px]"
                    : "max-h-0 top-[42px]"
                }`}
              >
                <div className="w-[24px] h-[24px] flex-none">
                  <RiveComponent />
                </div>
                <span className="text-silver text-sm font-archb px-[4px] whitespace-nowrap">
                  {"Referring" +
                    (buyTicketsClickable && referrer != undefined
                      ? ` ${truncateMiddle(referrer, 5, 3)}`
                      : "")}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1 select-none md:hidden block ml-2">
            <img
              src={ball}
              className="hover:scale-105 ~h-9/11 ~w-9/11 animate-bounce delay-750"
              alt="persian-pink-ball"
            />
          </div>
        </div>
        <div className="flex-1 select-none md:block hidden">
          <img
            src={ball}
            className="hover:scale-105 ~mt-3/4 ~h-9/11 ~w-9/11 animate-bounce delay-750"
            alt="persian-pink-ball"
          />
        </div>
      </div>
    </div>
  );
}
